export class Lagrange {
    constructor(points) {
        this.#set = points;
    }
    #set;
    #weights = null;
    _prepareBarycentric() {
        if (this.#weights) {
            return;
        }
        const points = this.#set || [];
        const n = points.length;
        if (n === 0) {
            this.#weights = [];
            return;
        }
        // Compute barycentric weights in a numerically stable way.
        // Direct product of differences can overflow/underflow for large n.
        // We compute log-weights and normalize by subtracting the max log to
        // keep values in a reasonable range (scaling cancels out in evaluation).
        const logs = new Array(n);
        const signs = new Array(n);
        for (let j = 0; j < n; j++) {
            let sumLog = 0;
            let sign = 1;
            const xj = points[j].x;
            for (let m = 0; m < n; m++) {
                if (m === j)
                    continue;
                const diff = xj - points[m].x;
                if (diff === 0) {
                    throw new Error('Invalid function provided.');
                }
                if (diff < 0)
                    sign = -sign;
                sumLog += Math.log(Math.abs(diff));
            }
            // log of weight is -sumLog
            logs[j] = -sumLog;
            signs[j] = sign;
        }
        // normalize logs to avoid underflow/overflow when exponentiating
        let maxLog = logs[0];
        for (let j = 1; j < n; j++) {
            if (logs[j] > maxLog)
                maxLog = logs[j];
        }
        const w = new Array(n);
        for (let j = 0; j < n; j++) {
            // scaled weight: sign * exp(log - maxLog)
            const scaled = Math.exp(logs[j] - maxLog);
            w[j] = signs[j] * scaled;
        }
        this.#weights = w;
    }
    _computeBarycentric(x) {
        const points = this.#set || [];
        if (!this.#weights) {
            this._prepareBarycentric();
        }
        const w = this.#weights || [];
        let numerator = 0;
        let denominator = 0;
        for (let j = 0; j < points.length; j++) {
            const xj = points[j].x;
            if (x === xj) {
                return points[j].y;
            }
            const temp = w[j] / (x - xj);
            numerator += temp * points[j].y;
            denominator += temp;
        }
        return numerator / denominator;
    }
    // Iterative/global Lagrange computation removed — barycentric is used
    // exclusively for interpolation.
    interpolate(x) {
        return this._computeBarycentric(x);
    }
    static create(...values) {
        var array = [];
        if (arguments.length > 0 && Array.isArray(arguments[0])) {
            array = arguments[0];
        }
        else {
            array = Array.from(values);
        }
        const points = array.map((y, x) => { return typeof y === 'number' ? { x, y } : y; });
        return new Lagrange(points);
    }
}
/**
 * @deprecated Use Lagrange instead. This alias is kept for backward compatibility.
 */
export const Lagrangian = {
    create: Lagrange.create
};
export class Pchip {
    constructor(points) {
        if (!points || points.length < 2)
            throw new Error('Need at least 2 points');
        const pts = points.slice().sort((a, b) => a.x - b.x);
        this.xs = pts.map(p => p.x);
        this.ys = pts.map(p => p.y);
        this.ms = this.computeDerivatives();
    }
    computeDerivatives() {
        const n = this.xs.length;
        const h = new Array(n - 1);
        const delta = new Array(n - 1);
        for (let i = 0; i < n - 1; i++) {
            h[i] = this.xs[i + 1] - this.xs[i];
            delta[i] = (this.ys[i + 1] - this.ys[i]) / h[i];
        }
        const m = new Array(n).fill(0);
        // endpoints: use one-sided slope
        m[0] = delta[0];
        m[n - 1] = delta[n - 2];
        // interior: weighted harmonic mean (PCHIP)
        for (let i = 1; i < n - 1; i++) {
            const d1 = delta[i - 1], d2 = delta[i];
            if (d1 === 0 || d2 === 0 || d1 * d2 <= 0) {
                m[i] = 0;
            }
            else {
                const w1 = 2 * h[i] + h[i - 1];
                const w2 = h[i] + 2 * h[i - 1];
                m[i] = (w1 + w2) / ((w1 / d1) + (w2 / d2));
            }
        }
        // (Optional) limit derivative to avoid overshoot: monotonicity constraints
        for (let i = 0; i < n - 1; i++) {
            if (delta[i] === 0) {
                m[i] = 0;
                m[i + 1] = 0;
            }
            else {
                const a = m[i] / delta[i];
                const b = m[i + 1] / delta[i];
                const s = a * a + b * b;
                if (s > 9) { // limit size, algorithm from Fritsch-Carlson/PCHIP
                    const tau = 3 / Math.sqrt(s);
                    m[i] = tau * a * delta[i];
                    m[i + 1] = tau * b * delta[i];
                }
            }
        }
        return m;
    }
    interpolate(x) {
        const xs = this.xs, ys = this.ys, ms = this.ms;
        const n = xs.length;
        // quick bounds
        if (x <= xs[0]) {
            // avoid dangerous extrapolation: use linear extension or return ys[0]
            const slope = ms[0];
            return ys[0] + slope * (x - xs[0]);
        }
        if (x >= xs[n - 1]) {
            const slope = ms[n - 1];
            return ys[n - 1] + slope * (x - xs[n - 1]);
        }
        // locate interval
        let i = 0, j = n - 1;
        while (j - i > 1) {
            const mid = (i + j) >> 1;
            if (xs[mid] <= x)
                i = mid;
            else
                j = mid;
        }
        const h = xs[i + 1] - xs[i];
        const t = (x - xs[i]) / h;
        const t2 = t * t, t3 = t2 * t;
        // Hermite basis
        const h00 = 2 * t3 - 3 * t2 + 1;
        const h10 = t3 - 2 * t2 + t;
        const h01 = -2 * t3 + 3 * t2;
        const h11 = t3 - t2;
        return h00 * ys[i] + h10 * (ms[i] * h) + h01 * ys[i + 1] + h11 * (ms[i + 1] * h);
    }
    static create(...values) {
        var array = [];
        if (arguments.length > 0 && Array.isArray(arguments[0])) {
            array = arguments[0];
        }
        else {
            array = Array.from(values);
        }
        const points = array.map((y, x) => { return typeof y === 'number' ? { x, y } : y; });
        return new Pchip(points);
    }
}
export class Newton {
    constructor(points) {
        if (!points || points.length === 0)
            throw new Error('Need at least 1 point');
        const pts = points.slice().sort((a, b) => a.x - b.x);
        this.xs = pts.map(p => p.x);
        const n = pts.length;
        // copy y values into coefficients array and convert in-place to divided differences
        const a = pts.map(p => p.y);
        for (let j = 1; j < n; j++) {
            for (let i = n - 1; i >= j; i--) {
                const denom = this.xs[i] - this.xs[i - j];
                if (denom === 0)
                    throw new Error('Invalid function provided.');
                a[i] = (a[i] - a[i - 1]) / denom;
            }
        }
        this.coeffs = a;
    }
    interpolate(x) {
        const xs = this.xs, a = this.coeffs;
        const n = a.length;
        if (n === 0)
            return NaN;
        // evaluate Newton form with nested multiplication (Horner-like)
        let result = a[n - 1];
        for (let i = n - 2; i >= 0; i--) {
            result = result * (x - xs[i]) + a[i];
        }
        return result;
    }
    static create(...values) {
        var array = [];
        if (arguments.length > 0 && Array.isArray(arguments[0])) {
            array = arguments[0];
        }
        else {
            array = Array.from(values);
        }
        const points = array.map((y, x) => { return typeof y === 'number' ? { x, y } : y; });
        return new Newton(points);
    }
}
//}
